import React from 'react';
import { motion } from 'framer-motion';
import { FaFlask, FaChartLine, FaUniversity, FaUsers, FaAward, FaMicroscope } from 'react-icons/fa';
import { IoMdRocket } from 'react-icons/io';
import { GiArtificialIntelligence, GiChemicalDrop } from 'react-icons/gi';
import { SiFuturelearn } from 'react-icons/si';
import './research.css';

const Research = () => {
  const researchAreas = [
    {
      id: 1,
      title: "Artificial Intelligence",
      description: "Advancing machine learning algorithms and AI applications for industry 4.0",
      icon: <GiArtificialIntelligence size={40} />,
      projects: 18
    },
    {
      id: 2,
      title: "Sustainable Energy",
      description: "Developing renewable energy solutions and smart grid technologies",
      icon: <IoMdRocket size={40} />,
      projects: 12
    },
    {
      id: 3,
      title: "Biotechnology",
      description: "Pioneering research in genetic engineering and pharmaceutical sciences",
      icon: <FaMicroscope size={40} />,
      projects: 9
    },
    {
      id: 4,
      title: "Advanced Materials",
      description: "Nanomaterials and smart materials for next-generation applications",
      icon: <GiChemicalDrop size={40} />,
      projects: 14
    }
  ];

  const stats = [
    { value: "45+", label: "Research Labs" },
    { value: "$28M", label: "Annual Funding" },
    { value: "120+", label: "Patents Filed" },
    { value: "300+", label: "Publications/Year" }
  ];

  const featuredProjects = [
    {
      title: "AI-Powered Urban Planning",
      description: "Developing neural networks to optimize city infrastructure development",
      team: "Dr. Chen Li (Lead), 8 researchers"
    },
    {
      title: "Quantum Computing Applications",
      description: "Exploring quantum algorithms for financial modeling and cryptography",
      team: "Prof. Rajesh Kumar (Lead), 12 researchers"
    },
    {
      title: "Biodegradable Electronics",
      description: "Creating environmentally friendly electronic components",
      team: "Dr. Mei Ling (Lead), 6 researchers"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div 
      className="research-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Hero Section */}
      <div className="research-hero">
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1><FaFlask /> Research Excellence at Asian Polytechnic</h1>
          <p className="hero-subtitle">Pushing boundaries through innovation and discovery in science and technology</p>
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-card"
                whileHover={{ scale: 1.05 }}
                variants={itemVariants}
              >
                <div className="stat-value-research">{stat.value}</div>
                <div className="stat-label-research">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Research Areas */}
      <section className="research-areas">
        <div className="section-header">
          <h2><FaUniversity /> Our Research Focus Areas</h2>
          <p>Interdisciplinary research addressing global challenges</p>
        </div>
        
        <motion.div 
          className="areas-grid"
          variants={containerVariants}
        >
          {researchAreas.map((area) => (
            <motion.div 
              key={area.id}
              className="area-card"
              variants={itemVariants}
              whileHover={{ 
                y: -5,
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)"
              }}
            >
              <div className="area-icon">{area.icon}</div>
              <h3>{area.title}</h3>
              <p>{area.description}</p>
              <div className="projects-count">
                <span>{area.projects} active projects</span>
              </div>
              <button className="explore-btn">Explore Area</button>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Featured Projects */}
      <section className="featured-projects">
        <div className="section-header">
          <h2><FaAward /> Spotlight Projects</h2>
          <p>Groundbreaking research initiatives making global impact</p>
        </div>
        
        <motion.div 
          className="projects-list"
          variants={containerVariants}
        >
          {featuredProjects.map((project, index) => (
            <motion.div 
              key={index}
              className="project-card"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <div className="project-header">
                <div className="project-number">0{index + 1}</div>
                <h3>{project.title}</h3>
              </div>
              <p className="project-desc">{project.description}</p>
              <div className="project-team">
                <FaUsers /> {project.team}
              </div>
              <button className="project-details">View Details</button>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Research Collaboration */}
      <section className="collaboration-section">
        <motion.div 
          className="collab-content"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2><FaUsers /> Global Research Partnerships</h2>
          <p>We collaborate with leading institutions and industries worldwide to drive innovation and solve complex problems through interdisciplinary research.</p>
          <div className="partners-logos">
            {/* These would be actual logo images in production */}
            <div className="partner-logo">MIT</div>
            <div className="partner-logo">NUS</div>
            <div className="partner-logo">Samsung</div>
            <div className="partner-logo">TSMC</div>
          </div>
        </motion.div>
      </section>

      {/* Future Directions */}
      <section className="future-section">
        <div className="future-content">
          <SiFuturelearn size={60} />
          <h2>Shaping the Future Through Research</h2>
          <p>Our 2030 Research Vision focuses on sustainable development, digital transformation, and human-centered technologies that will define the next decade.</p>
          <button className="cta-button">View Research Roadmap</button>
        </div>
      </section>
    </motion.div>
  );
};

export default Research;